import { createFileRoute } from '@tanstack/react-router';
import { ActionIcon, Button, Center, Group, Loader, SimpleGrid, Text, TextInput, Tooltip } from '@mantine/core';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import {
  IconCheck, IconDeviceFloppy, IconLabel, IconPencil, IconRefresh, IconRotate2, IconSearch, IconTags, IconX,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { notifyError } from '@/shared/lib/notify';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { httpRequest } from '@/shared/api/client';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { StatCard } from '@/shared/components/StatCard';
import { mw, MW_CARD_SHADOW, MW_CODE_STYLE } from '@/shared/mobile/tokens';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { MobileStructureLabels } from '@/features/admin/mobile/MobileStructureLabels';

export const Route = createFileRoute('/_app/admin/structure-labels')({ component: StructureLabelsRoute });

/** Selettore: etichette struttura mobile (MaeDesign) vs griglia desktop. */
function StructureLabelsRoute() {
  const isMobile = useIsMobileShell();
  return isMobile ? <MobileStructureLabels /> : <StructureLabelsPage />;
}

interface StructureLabel { key: string; defaultLabel: string; customLabel?: string | null; }

function StructureLabelsPage() {
  const qc = useQueryClient();
  const labelsQ = useQuery({
    queryKey: ['structure-labels'],
    queryFn: () => httpRequest<StructureLabel[]>('/api/structure-labels', { endpointType: 'plain' }),
  });

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const labels = useMemo(() => labelsQ.data ?? [], [labelsQ.data]);

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const l of labels) next[l.key] = l.customLabel ?? '';
    setDrafts(next);
  }, [labels]);

  const save = useMutation({
    mutationFn: (args: { key: string; value: string }) =>
      httpRequest(`/api/structure-labels/${encodeURIComponent(args.key)}`, {
        method: 'PATCH',
        body: { value: args.value },
        endpointType: 'plain',
      }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Etichetta salvata' });
      qc.invalidateQueries({ queryKey: ['structure-labels'] });
    },
    onError: (err) => notifyError(err, 'Salvataggio fallito'),
    onSettled: () => setSavingKey(null),
  });

  const doSave = (key: string, value: string) => { setSavingKey(key); save.mutate({ key, value }); };

  const isDirty = (l: StructureLabel) => (drafts[l.key] ?? '') !== (l.customLabel ?? '');
  const isCustom = (l: StructureLabel) => Boolean((l.customLabel ?? '').trim());

  const totals = useMemo(() => {
    const customized = labels.filter(isCustom).length;
    const dirty = labels.filter(isDirty).length;
    return { total: labels.length, customized, standard: labels.length - customized, dirty };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels, drafts]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return labels;
    return labels.filter((l) =>
      l.key.toLowerCase().includes(term)
      || (l.defaultLabel ?? '').toLowerCase().includes(term)
      || (l.customLabel ?? '').toLowerCase().includes(term),
    );
  }, [labels, search]);

  const card: CSSProperties = {
    border: `1px solid ${mw.line}`, borderRadius: 16, background: mw.surface, boxShadow: MW_CARD_SHADOW,
  };
  const chip = (label: string, bg: string, color: string, code = false) => (
    <span style={{ display: 'inline-block', fontSize: code ? 'var(--fs-compact)' : 'var(--fs-compact)', fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: bg, color, ...(code ? MW_CODE_STYLE : null), whiteSpace: 'nowrap' }}>{label}</span>
  );

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconTags size={22} />}
        title="Etichette di struttura organizzativa"
        sub="Rinomina le entità organizzative mostrate nella UI della tua azienda."
        tone="bordeaux"
        actions={
          <Tooltip label="Aggiorna">
            <ActionIcon variant="light" color="dark" size="lg" aria-label="Aggiorna etichette"
              onClick={() => qc.invalidateQueries({ queryKey: ['structure-labels'] })}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        }
      />

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing={14} mb={18}>
        <StatCard label="Etichette totali" value={totals.total} icon={IconTags} color="brand" loading={labelsQ.isLoading} />
        <StatCard label="Personalizzate" value={totals.customized} icon={IconPencil} color="blue" loading={labelsQ.isLoading} />
        <StatCard label="Al default" value={totals.standard} icon={IconLabel} color="gray" loading={labelsQ.isLoading} />
        <StatCard label="Modifiche non salvate" value={totals.dirty} icon={IconDeviceFloppy} color={totals.dirty > 0 ? 'orange' : 'green'} loading={labelsQ.isLoading} />
      </SimpleGrid>

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', border: `1px solid ${mw.line}`, background: mw.accentTint, borderRadius: 14, padding: '12px 16px', marginBottom: 16 }}>
        <IconTags size={18} style={{ color: mw.accentText, flexShrink: 0, marginTop: 1 }} />
        <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, lineHeight: 1.5 }}>
          Personalizza il nome delle entità organizzative (es. chiama <strong>«Reparto»</strong> invece di <strong>«Area»</strong>). Lascia vuoto — o usa <em>Reimposta</em> — per tornare all&apos;etichetta di default.
        </Text>
      </div>

      <div style={{ ...card, padding: '14px 16px', marginBottom: 16 }}>
        <TextInput
          placeholder="Cerca per chiave o etichetta…"
          leftSection={<IconSearch size={14} />}
          rightSection={search ? (
            <ActionIcon variant="subtle" size="sm" aria-label="Cancella ricerca" onClick={() => setSearch('')}>
              <IconX size={12} />
            </ActionIcon>
          ) : null}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          w={320}
        />
      </div>

      {labelsQ.isError ? <QueryErrorAlert error={labelsQ.error} onRetry={() => { void labelsQ.refetch(); }} /> : labelsQ.isLoading ? (
        <Center py={60}><Loader /></Center>
      ) : filtered.length === 0 ? (
        <div style={{ ...card, padding: '28px 20px', textAlign: 'center' }}>
          <Text style={{ fontSize: 'var(--fs-body)', color: mw.ink500 }}>
            {labels.length === 0 ? 'Nessuna etichetta configurabile.' : 'Nessun risultato per la ricerca.'}
          </Text>
        </div>
      ) : (
        <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }} spacing={14}>
          {filtered.map((l) => {
            const dirty = isDirty(l);
            const custom = isCustom(l);
            const saving = save.isPending && savingKey === l.key;
            return (
              <div key={l.key} style={{ ...card, padding: 18, display: 'flex', flexDirection: 'column', gap: 12, borderColor: dirty ? mw.warning : mw.line }}>
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  {chip(l.key, mw.surface2, mw.ink900, true)}
                  {dirty
                    ? chip('● Non salvata', mw.warningBg, mw.warning)
                    : custom
                      ? chip('Personalizzata', mw.accentTint, mw.accentText)
                      : chip('Default', mw.surface2, mw.ink500)}
                </Group>

                <div>
                  <div style={{ fontSize: 'var(--fs-compact)', fontWeight: 500, letterSpacing: '.08em', textTransform: 'uppercase', color: mw.ink300, marginBottom: 3 }}>Default</div>
                  <Text style={{ fontSize: 'var(--fs-body)', fontWeight: 600, color: mw.ink900 }}>{l.defaultLabel}</Text>
                </div>

                <TextInput
                  label="Etichetta per l’azienda"
                  placeholder={l.defaultLabel}
                  value={drafts[l.key] ?? ''}
                  onChange={(e) => setDrafts((d) => ({ ...d, [l.key]: e.currentTarget.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter' && dirty) doSave(l.key, drafts[l.key] ?? ''); }}
                />

                <Group gap={8} justify="flex-end" mt="auto">
                  {custom && (
                    <Button
                      variant="subtle"
                      color="dark"
                      size="xs"
                      leftSection={<IconRotate2 size={13} />}
                      disabled={saving}
                      onClick={() => { setDrafts((d) => ({ ...d, [l.key]: '' })); doSave(l.key, ''); }}
                    >
                      Reimposta
                    </Button>
                  )}
                  <Button
                    size="xs"
                    leftSection={<IconCheck size={13} />}
                    loading={saving}
                    disabled={!dirty}
                    style={dirty ? { background: mw.brandFill } : undefined}
                    onClick={() => doSave(l.key, drafts[l.key] ?? '')}
                  >
                    Salva
                  </Button>
                </Group>
              </div>
            );
          })}
        </SimpleGrid>
      )}
    </MaeShell>
  );
}
